// Peak levels of the audio tracks that hls.js doesn't play.
//
// hls.js plays only the first ADTS AAC track of an MPEG-TS stream. This takes the same fragments
// (from the fragment loader, before hls.js gets them), picks the other ADTS AAC tracks, decodes them
// with WebCodecs and keeps the sample peak of every AAC frame with its PTS. peaks() returns the levels
// for a range of media time; hls.js media time = PTS - initPTS of the fragment's discontinuity (cc).
// LATM AAC (stream type 0x11) isn't supported.

const PACKET = 188;
const MAX_FRAMES = 2500; // ~50 s per track at 48 kHz
const SAMPLE_RATES = [96000, 88200, 64000, 48000, 44100, 32000, 24000, 22050, 16000, 12000, 11025, 8000, 7350];

export function extraAudioTracksSupported() {
	return typeof(window.AudioDecoder) === "function" && typeof(window.EncodedAudioChunk) === "function";
}

function packet_pid(u8, o) {
	return ((u8[o + 1] & 0x1f) << 8) | u8[o + 2];
}

function payload_start(u8, o) {
	return u8[o + 3] & 0x20 ? o + 5 + u8[o + 4] : o + 4;
}

// PMT PID and the ADTS AAC PIDs in PMT order
function audio_pids(u8) {
	var pmt = -1;
	for (var o = 0; o + PACKET <= u8.length; o += PACKET) {
		if (u8[o] !== 0x47 || !(u8[o + 1] & 0x40)) continue;
		var pid = packet_pid(u8, o);
		var p = payload_start(u8, o);
		p += u8[p] + 1;
		if (pid === 0 && pmt < 0) {
			pmt = packet_pid(u8, p + 9);
		} else if (pid === pmt) {
			var end = p + 3 + (((u8[p + 1] & 0x0f) << 8) | u8[p + 2]) - 4;
			var q = p + 12 + (((u8[p + 10] & 0x0f) << 8) | u8[p + 11]);
			var pids = [];
			while (q + 5 <= end) {
				if (u8[q] === 0x0f) pids.push(packet_pid(u8, q));
				q += 5 + (((u8[q + 3] & 0x0f) << 8) | u8[q + 4]);
			}
			return pids;
		}
	}
	return null;
}

// 33-bit PTS in seconds
function read_pts(u8, p) {
	var pts = (u8[p] & 0x0e) * 536870912 + // 1 << 29
		(u8[p + 1] << 22) + ((u8[p + 2] & 0xfe) << 14) +
		(u8[p + 3] << 7) + ((u8[p + 4] & 0xfe) >> 1);
	return pts / 90000;
}

class Track {
	constructor (pid, on_error) {
		this.pid = pid;
		this.pes = [];
		this.pes_cc = 0;
		this.decoder = null;
		this.config_key = "";
		this.channels = 0;
		this.pending_cc = [];
		// {t: PTS in seconds, cc, peaks: Float32Array}
		this.frames = [];
		this.on_error = on_error;
	}
	add_payload (u8, start, end, pusi, cc) {
		if (pusi) this.flush();
		if (!pusi && !this.pes.length) return;
		this.pes.push(u8.slice(start, end));
		this.pes_cc = cc;
	}
	flush () {
		if (!this.pes.length) return;
		var parts = this.pes;
		this.pes = [];
		var len = parts.reduce((s, p) => s + p.length, 0);
		var data = new Uint8Array(len);
		var o = 0;
		parts.forEach((p) => { data.set(p, o); o += p.length; });
		if (len < 9 || data[0] !== 0 || data[1] !== 0 || data[2] !== 1) return;
		var pts = data[7] & 0x80 ? read_pts(data, 9) : null;
		if (pts === null) return;
		this.parse_adts(data.subarray(9 + data[8]), pts, this.pes_cc);
	}
	parse_adts (data, pts, cc) {
		var i = 0;
		var frame = 0;
		while (i + 7 <= data.length) {
			if (data[i] !== 0xff || (data[i + 1] & 0xf6) !== 0xf0) {
				i++;
				continue;
			}
			var header = data[i + 1] & 0x01 ? 7 : 9;
			var profile = (data[i + 2] >> 6) + 1;
			var sr_index = (data[i + 2] >> 2) & 0x0f;
			var channels = ((data[i + 2] & 0x01) << 2) | (data[i + 3] >> 6);
			var length = ((data[i + 3] & 0x03) << 11) | (data[i + 4] << 3) | (data[i + 5] >> 5);
			var rate = SAMPLE_RATES[sr_index];
			if (!rate || !channels || length < header || i + length > data.length) break;
			this.configure(profile, sr_index, channels, rate);
			if (this.decoder) {
				var t = pts + frame * 1024 / rate;
				this.pending_cc.push(cc);
				try {
					this.decoder.decode(new EncodedAudioChunk({type: "key", timestamp: Math.round(t * 1e6), data: data.subarray(i + header, i + length)}));
				} catch (err) {
					this.pending_cc.pop();
					this.reset_decoder();
				}
			}
			frame++;
			i += length;
		}
	}
	configure (profile, sr_index, channels, rate) {
		var key = profile + ":" + sr_index + ":" + channels;
		if (this.decoder && key === this.config_key) return;
		this.reset_decoder();
		this.config_key = key;
		this.channels = channels;
		// AudioSpecificConfig: object type, sampling frequency index, channel configuration
		var asc = new Uint8Array([(profile << 3) | (sr_index >> 1), ((sr_index & 1) << 7) | (channels << 3)]);
		try {
			this.decoder = new AudioDecoder({
				output: (data) => this.on_output(data),
				error: (err) => {
					this.on_error(err);
					this.decoder = null;
					this.config_key = "";
				}
			});
			this.decoder.configure({codec: "mp4a.40." + profile, sampleRate: rate, numberOfChannels: channels, description: asc});
		} catch (err) {
			this.on_error(err);
			this.decoder = null;
		}
	}
	reset_decoder () {
		if (this.decoder && this.decoder.state !== "closed") {
			try {
				this.decoder.close();
			} catch (err) {
				// Already closed
			}
		}
		this.decoder = null;
		this.config_key = "";
		this.pending_cc = [];
	}
	on_output (data) {
		var cc = this.pending_cc.length ? this.pending_cc.shift() : 0;
		var n = data.numberOfChannels;
		var peaks = new Float32Array(n);
		var buf = new Float32Array(data.numberOfFrames);
		for (var c = 0; c < n; c++) {
			data.copyTo(buf, {planeIndex: c, format: "f32-planar"});
			var p = 0;
			for (var i = 0; i < buf.length; i++) {
				var v = buf[i] < 0 ? -buf[i] : buf[i];
				if (v > p) p = v;
			}
			peaks[c] = p;
		}
		var t = data.timestamp / 1e6;
		data.close();
		this.frames.push({t: t, cc: cc, peaks: peaks});
		if (this.frames.length > MAX_FRAMES + 500) this.frames.splice(0, this.frames.length - MAX_FRAMES);
	}
	close () {
		this.reset_decoder();
		this.frames = [];
		this.pes = [];
	}
}

export class ExtraAudioTracks {
	constructor () {
		this.tracks = [];
		this.pids_key = "";
		this.init_pts = {};
	}
	// Raw TS data of a fragment; must be called before hls.js gets (and detaches) the buffer
	push (buffer, cc) {
		var u8 = new Uint8Array(buffer);
		var pids = audio_pids(u8);
		if (!pids) return;
		// The first ADTS track is played by hls.js
		var extra = pids.slice(1);
		var key = extra.join(",");
		if (key !== this.pids_key) {
			this.tracks.forEach(t => t.close());
			this.tracks = extra.map(pid => new Track(pid, (err) => console.warn("[ExtraAudioTracks] decoder error", err)));
			this.pids_key = key;
		}
		if (!this.tracks.length) return;
		var by_pid = {};
		this.tracks.forEach(t => { by_pid[t.pid] = t; });
		for (var o = 0; o + PACKET <= u8.length; o += PACKET) {
			if (u8[o] !== 0x47) continue;
			var track = by_pid[packet_pid(u8, o)];
			if (!track || !(u8[o + 3] & 0x10)) continue;
			var start = payload_start(u8, o);
			if (start < o + PACKET) track.add_payload(u8, start, o + PACKET, !!(u8[o + 1] & 0x40), cc);
		}
		// A PES that ends the fragment is complete when its length says so; otherwise the next PES flushes it
		this.tracks.forEach((t) => {
			if (!t.pes.length) return;
			var first = t.pes[0];
			var pes_len = first.length >= 6 ? (first[4] << 8) | first[5] : 0;
			var have = t.pes.reduce((s, p) => s + p.length, 0);
			if (pes_len && have >= pes_len + 6) t.flush();
		});
	}
	set_init_pts (cc, base_time, timescale) {
		this.init_pts[cc] = base_time / timescale;
	}
	// Channel counts per extra track (0 until the first frame is decoded)
	layout () {
		return this.tracks.map(t => t.channels);
	}
	// Highest peak per channel of every extra track for media time (from, to]
	peaks (from, to) {
		return this.tracks.map((track) => {
			var res = new Array(track.channels).fill(0);
			var frames = track.frames;
			for (var i = frames.length - 1; i >= 0; i--) {
				var f = frames[i];
				var offset = this.init_pts[f.cc];
				if (offset === undefined) continue;
				var t = f.t - offset;
				if (t > to) continue;
				if (t <= from) break;
				for (var c = 0; c < res.length && c < f.peaks.length; c++) {
					if (f.peaks[c] > res[c]) res[c] = f.peaks[c];
				}
			}
			return res;
		});
	}
	reset () {
		this.tracks.forEach(t => t.close());
		this.tracks = [];
		this.pids_key = "";
		this.init_pts = {};
	}
}
